import { HTMLCustomElement } from '@emartech/ui-framework-utils';

class EIframeFit extends HTMLCustomElement {
  init() {
    super.watchForAddedChildNodes({ subtree: true, childList: true });

    this._scrollable = false;

    this._dom = {};
    this._dom.iframe = null;

    this._events = {};
    this._events.fit = this.fit.bind(this);
  };

  connectedCallback() {
    this._dom.iframe = this.querySelector('iframe');

    if (this._dom.iframe) {
      this._setupIframe();
    }
  }

  childrenChangedCallback(nodes) {
    const foundIframeElementList = nodes.filter(node => node.length && node[0].nodeName === 'IFRAME');
    const foundIframeElement = foundIframeElementList.length ? foundIframeElementList[0][0] : null;
    if (!foundIframeElement) {
      return;
    }

    this._dom.iframe = foundIframeElement;
    this._setupIframe();
  }

  disconnectedCallback() {
    if (this._dom.iframe) {
      this._dom.iframe.removeEventListener('load', this._events.fit);
    }
  }

  static get observedAttributes() {
    return ['scrollable'];
  }

  set scrollable(value) {
    this._scrollable = this._convertAttributeToBoolean(value);
    this.fit();
  }

  fit(event, size) {
    if (!this._dom.iframe) {
      return;
    }

    const documentSize = size || this._getIframeSize();
    if (!documentSize) {
      return;
    }

    const iframeProperties = this._getIframeProperties(documentSize);

    this._css(this._dom.iframe.querySelector('iframe') || this._dom.iframe, iframeProperties);
  }

  _css(element, properties) {
    Object.keys(properties).forEach((property) => {
      element.style[property] = properties[property];
    });
  }

  _getIframeProperties(documentSize) {
    const scaleRatio = this._calculateScaleRatio(documentSize);
    const scaledContainerWidth = 1 / scaleRatio * this.offsetWidth;
    const scaledContainerHeight = 1 / scaleRatio * this.offsetHeight;
    const finalContainerHeight = this._scrollable ? scaledContainerHeight : documentSize.height;

    const iframeProperties = {
      position: 'absolute',
      width: `${documentSize.width}px`,
      height: `${finalContainerHeight}px`,
      transformOrigin: '0 0',
      opacity: 1,
      left: this._scrollable ? '0' : '50%',
      top: this._scrollable ? '0' : '50%',
      transform: this._scrollable ? `scale(${scaleRatio})` : `scale(${scaleRatio}) translate(-50%, -50%)`,
      minWidth: this._scrollable ? `${scaledContainerWidth}px` : `${documentSize.width}px`
    };
    return iframeProperties;
  }

  _setupIframe() {
    this._dom.iframe.style.opacity = 0;
    this._dom.iframe.style.transition = 'opacity .3s';

    this._dom.iframe.addEventListener('load', this._events.fit);
  }

  _getCrossdomainDocumentSize(iframeWindow) {
    window.addEventListener('message', function(event) {
      if (event.source !== iframeWindow) {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        this.fit(event, {
          width: data.result.width,
          height: data.result.height
        });
      } catch (error) {
        return false;
      }
    }.bind(this));

    iframeWindow.postMessage(JSON.stringify({
      method: 'e:getDocumentSize'
    }), this._dom.iframe.src);
  }

  _getIframeSize() {
    const iframeWindow = this._dom.iframe.contentWindow;
    const originalStyle = [];
    let iframeDocument;

    try {
      iframeDocument = this._dom.iframe.contentDocument;
    } catch (exception) {
      this._getCrossdomainDocumentSize(iframeWindow);
      return false;
    }

    originalStyle.iframeHTMLHeight = iframeDocument.documentElement.style.height;
    iframeDocument.documentElement.style.height = 'auto';

    const iframeBody = iframeDocument.body;
    originalStyle.iframeBody = iframeBody.getAttribute('style') || '';
    originalStyle.component = this.getAttribute('style') || '';

    this._addTemporaryIframeStyle(iframeBody);
    this._addTemporaryComponentStyle();

    const documentSize = {
      width: iframeBody.scrollWidth,
      height: iframeBody.offsetHeight
    };

    this._restoreOriginalStyle(iframeDocument, originalStyle);
    return documentSize;
  }

  _addTemporaryIframeStyle(iframeBody) {
    this._dom.iframe.style.width = 0;
    this._dom.iframe.style.height = 0;

    iframeBody.style.margin = 0;
    iframeBody.style.padding = 0;
    iframeBody.style.float = 'left';
    iframeBody.style.width = 'auto';
    iframeBody.style.height = 'auto';
  }

  _addTemporaryComponentStyle() {
    this.style.position = 'fixed';
    this.style.width = 0;
    this.style.height = 0;
    this.style.left = 0;
    this.style.top = '-9999px';
  }

  _restoreOriginalStyle(iframeDocument, original) {
    iframeDocument.documentElement.style.height = original.iframeHTMLHeight;
    iframeDocument.body.setAttribute('style', original.iframeBody);
    this.setAttribute('style', original.component);
  }

  _calculateScaleRatio(documentSize) {
    const widthRatio = this.offsetWidth / documentSize.width;
    const heightRatio = this.offsetHeight / documentSize.height;

    const scaleRatio = widthRatio < heightRatio || this._scrollable ? widthRatio : heightRatio;

    return scaleRatio > 1 ? 1 : scaleRatio;
  }

}

export default EIframeFit;
